import { useRef, useState } from 'react'
import { env } from '../lib/env.js'
import { Button } from './Button.js'
import { CameraCaptureDialog } from './CameraCaptureDialog.js'

interface ImageUploadProps {
  /** Current stored image URL, if any. */
  value: string | null
  onChange: (url: string | null) => void
  /** Label for the upload button. */
  label?: string
  /**
   * `capture` hints mobile browsers to open the camera directly. Kept for
   * callers that want camera-only.
   */
  capture?: boolean
  /**
   * Offer a dedicated "Take photo" (camera) button ALONGSIDE the gallery
   * button. Use for the student photo so an admin enrolling a child in person
   * can shoot a photo directly. A single accept="image/*" picker is supposed to
   * surface the camera itself, but in-app/WebView browsers (e.g. a link opened
   * inside WhatsApp) and some Android OEM pickers strip it — an explicit
   * capture="environment" input is the reliable way to reach the camera.
   */
  allowCamera?: boolean
}

/**
 * Uploads a single image to the API's R2-backed /uploads endpoint and reports
 * the stored URL. Camera-capable on mobile via the `capture` attribute. Used
 * for the student profile photo and academic-history report-card proofs.
 */
export function ImageUpload({
  value,
  onChange,
  label = 'Upload image',
  capture,
  allowCamera = false,
}: ImageUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cameraOpen, setCameraOpen] = useState(false)

  const onPick = async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`${env.API_URL}/api/v1/uploads`, {
        method: 'POST',
        // Cookie-based auth: send the session cookie cross-subdomain. Don't set
        // Content-Type — the browser sets the multipart boundary for FormData.
        credentials: 'include',
        body: form,
      })
      const json = (await res.json()) as
        | { data: { url: string } }
        | { error: { message: string } }
      if (!res.ok || 'error' in json) {
        throw new Error('error' in json ? json.error.message : 'Upload failed')
      }
      onChange(json.data.url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-2">
      {value ? (
        <div className="flex items-center gap-3">
          <img
            src={value}
            alt="Uploaded preview"
            className="h-16 w-16 rounded-md border border-border object-cover"
          />
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-fg-muted hover:text-danger"
          >
            Remove
          </button>
        </div>
      ) : null}
      {/* Gallery / files picker. Broad `image/*` (not a specific MIME list) so a
          first-party mobile browser also offers the camera here — a narrow
          "image/jpeg,image/png,image/webp" list suppresses the camera on Android
          Chrome. The server still enforces JPEG/PNG/WebP (uploads.ts), so this
          only widens the on-device chooser, not what's actually accepted. */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        {...(capture ? { capture: 'environment' as const } : {})}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void onPick(file)
          e.target.value = ''
        }}
      />
      <div className="flex flex-wrap items-center gap-2">
        {allowCamera ? (
          // In-app webcam/camera capture (getUserMedia) — works on laptops AND
          // mobile, unlike <input capture> which only hints the OS camera on
          // phones and is ignored on desktop.
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => setCameraOpen(true)}
          >
            Take photo
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={busy}
          onClick={() => inputRef.current?.click()}
        >
          {value ? 'Replace' : allowCamera ? 'Choose from gallery' : label}
        </Button>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}

      {allowCamera ? (
        <CameraCaptureDialog
          open={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onCapture={(file) => void onPick(file)}
        />
      ) : null}
    </div>
  )
}
